import type React from "react";
import { createContext, useContext, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";

type FootnotesContextValue = {
	registerRef: (id: string) => void;
	registerDef: (id: string, content: React.ReactNode) => void;
	getOrder: () => readonly string[];
	getDisplayOrder: () => string[];
	getDef: (id: string) => React.ReactNode | undefined;
};

const FootnotesContext = createContext<FootnotesContextValue | null>(null);

function useFootnotes(): FootnotesContextValue {
	const ctx = useContext(FootnotesContext);
	if (!ctx) {
		throw new Error(
			"Footnote, FootnoteRef, and Footnotes require FootnotesProvider (CustomMDX includes it).",
		);
	}
	return ctx;
}

/**
 * Wrap MDX content so FootnoteRef / Footnotes / Footnote can register in document order.
 * Resets registry each render (SSR and Strict Mode safe).
 */
export function FootnotesProvider({ children }: { children: React.ReactNode }) {
	const orderRef = useRef<string[]>([]);
	const refSeenRef = useRef(new Set<string>());
	const defsRef = useRef(new Map<string, React.ReactNode>());
	const defOrderRef = useRef<string[]>([]);
	const defSeenRef = useRef(new Set<string>());

	orderRef.current = [];
	refSeenRef.current = new Set();
	defsRef.current = new Map();
	defOrderRef.current = [];
	defSeenRef.current = new Set();

	const value = useMemo<FootnotesContextValue>(
		() => ({
			registerRef(id: string) {
				if (!refSeenRef.current.has(id)) {
					refSeenRef.current.add(id);
					orderRef.current.push(id);
				}
			},
			registerDef(id: string, content: React.ReactNode) {
				defsRef.current.set(id, content);
				if (!defSeenRef.current.has(id)) {
					defSeenRef.current.add(id);
					defOrderRef.current.push(id);
				}
			},
			getOrder: () => orderRef.current,
			getDisplayOrder: () => {
				const refOrder = orderRef.current;
				const withDef = refOrder.filter((i) => defsRef.current.has(i));
				const orphan = defOrderRef.current.filter((i) => !refOrder.includes(i));
				return [...withDef, ...orphan];
			},
			getDef: (id: string) => defsRef.current.get(id),
		}),
		[],
	);

	return (
		<FootnotesContext.Provider value={value}>
			{children}
		</FootnotesContext.Provider>
	);
}

export type FootnoteRefProps = {
	/** Stable slug (`kebab-case` recommended). Must match a `<Footnote id={...} />`. */
	id: string;
	className?: string;
};

/** Superscript link to the footnote list (`#fn-{id}`). Numbers follow first-reference order. */
export function FootnoteRef({ id, className }: FootnoteRefProps) {
	const { registerRef, getOrder } = useFootnotes();
	registerRef(id);
	const n = getOrder().indexOf(id) + 1;

	return (
		<sup
			className={cn(
				"not-prose ms-0.5 align-super text-[0.85em] font-medium leading-none",
				className,
			)}
		>
			<a
				href={`#fn-${id}`}
				id={`fnref-${id}`}
				className="text-primary decoration-primary/50 underline-offset-[3px] hover:underline"
			>
				{n}
			</a>
		</sup>
	);
}

export type FootnoteProps = {
	id: string;
	children: React.ReactNode;
};

/**
 * Registers footnote body. Renders nothing in place — content appears in `<Footnotes>`.
 * Place `<Footnote>` elements inside `<Footnotes>...</Footnotes>`.
 */
export function Footnote({ id, children }: FootnoteProps) {
	const { registerDef } = useFootnotes();
	registerDef(id, children);
	return null;
}

export type FootnotesProps = {
	children: React.ReactNode;
	heading?: string;
	className?: string;
};

function FootnotesList({
	heading,
	className,
}: {
	heading: string;
	className?: string;
}) {
	const { getDisplayOrder, getDef } = useFootnotes();
	const filtered = getDisplayOrder().filter((i) => getDef(i) !== undefined);

	if (filtered.length === 0) {
		return null;
	}

	return (
		<section
			className={cn("not-prose mt-12 border-t border-border pt-8", className)}
			aria-label={heading}
		>
			<h2
				className="font-heading mb-4 scroll-mt-24 text-lg font-semibold tracking-tight text-foreground"
				id="footnotes"
			>
				{heading}
			</h2>
			<ol className="list-none space-y-3 ps-0">
				{filtered.map((id, index) => (
					<li
						key={id}
						id={`fn-${id}`}
						className="flex scroll-mt-24 gap-3 text-sm leading-relaxed text-muted-foreground"
					>
						<span
							className="mt-0.5 flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-foreground"
							aria-hidden
						>
							{index + 1}
						</span>
						<div className="min-w-0 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_p]:my-0 [&_p]:inline">
							{getDef(id)}
						</div>
						<a
							href={`#fnref-${id}`}
							className="ml-1 shrink-0 text-foreground/60 hover:text-foreground"
							aria-label="Back to reference"
						>
							↩
						</a>
					</li>
				))}
			</ol>
		</section>
	);
}

/**
 * Renders the footnote list: use once per article after body content.
 * Children should be `<Footnote id="...">...</Footnote>` entries.
 */
export function Footnotes({
	children,
	heading = "Footnotes",
	className,
}: FootnotesProps) {
	return (
		<>
			{children}
			<FootnotesList heading={heading} className={className} />
		</>
	);
}
